import React from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { Input } from "@core/components/ui/input";
import { Switch } from "@core/components/ui/switch";
import type { SettingDescriptor, SettingKey } from "./types";

interface SettingEditorProps {
  setting: SettingDescriptor;
  /** Commit la nouvelle valeur — referme la row */
  onCommit: (key: SettingKey, value: unknown) => void;
  /** Annule l'édition (Escape ou ×) — referme sans commit */
  onCancel: () => void;
}

/**
 * Editeur polymorphe inline pour une `SettingRow` expandée.
 * - `enum` → 3 cards radio stackées (active = violet)
 * - `string` → Input + bouton ✓
 * - `boolean` → Switch (commit instantané)
 * - `date` → Input type="date" + bouton ✓
 * Cf. plan §5.
 */
const SettingEditor: React.FC<SettingEditorProps> = ({
  setting,
  onCommit,
  onCancel,
}) => {
  if (setting.type === "enum") {
    return (
      <EnumEditor setting={setting} onCommit={onCommit} onCancel={onCancel} />
    );
  }
  if (setting.type === "boolean") {
    return <BooleanEditor setting={setting} onCommit={onCommit} />;
  }
  if (setting.type === "date") {
    return (
      <DateEditor setting={setting} onCommit={onCommit} onCancel={onCancel} />
    );
  }
  // Default = string
  return (
    <StringEditor setting={setting} onCommit={onCommit} onCancel={onCancel} />
  );
};

/** Enum : 3+ cards radio stackées avec icon + label + description */
const EnumEditor: React.FC<SettingEditorProps> = ({ setting, onCommit }) => {
  const options = setting.options ?? [];
  const current = setting.value;
  return (
    <div
      data-testid={`setting-editor-${setting.key}`}
      data-type="enum"
      className="mt-2 flex flex-col gap-1.5"
    >
      {options.map((o) => {
        const active = current === o.id;
        return (
          <button
            key={o.id}
            type="button"
            data-testid={`setting-editor-${setting.key}-option-${o.id}`}
            data-active={active ? "true" : undefined}
            onClick={() => onCommit(setting.key, o.id)}
            className={`flex items-start gap-3 rounded-[9px] border px-3 py-2.5 text-left transition-colors ${
              active
                ? "border-primary bg-primary/[0.16]"
                : "border-border bg-card hover:bg-card/80"
            }`}
          >
            <span
              aria-hidden
              className={`mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] ${
                active ? "border-primary bg-primary" : "border-border"
              }`}
            >
              {active && (
                <span className="block h-[5px] w-[5px] rounded-full bg-primary-foreground" />
              )}
            </span>
            <span className="text-[14px] leading-none mt-px">{o.icon}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-semibold text-foreground">
                {o.label}
              </span>
              <span className="block text-[11px] text-muted-foreground">
                {o.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

/** Boolean : Switch instant commit */
const BooleanEditor: React.FC<Omit<SettingEditorProps, "onCancel">> = ({
  setting,
  onCommit,
}) => {
  const { t } = useTranslation("polls");
  const current = setting.value === "true";
  return (
    <div
      data-testid={`setting-editor-${setting.key}`}
      data-type="boolean"
      className="mt-2 flex items-center gap-3 rounded-[9px] border border-border bg-card px-3 py-2"
    >
      <Switch
        checked={current}
        onCheckedChange={(v) => onCommit(setting.key, v)}
        data-testid={`setting-editor-${setting.key}-switch`}
      />
      <span className="text-[12px] text-muted-foreground">
        {current
          ? t("prePublish.settingEditor.boolean.on")
          : t("prePublish.settingEditor.boolean.off")}
      </span>
    </div>
  );
};

/** String : Input + bouton ✓ / Escape pour cancel */
const StringEditor: React.FC<SettingEditorProps> = ({
  setting,
  onCommit,
  onCancel,
}) => {
  const { t } = useTranslation("polls");
  const [value, setValue] = React.useState<string>(setting.value ?? "");
  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    onCommit(setting.key, value.trim());
  };
  const placeholderKey = settingPlaceholder(setting.key);
  return (
    <form
      data-testid={`setting-editor-${setting.key}`}
      data-type="string"
      onSubmit={handleSubmit}
      className="mt-2 flex items-center gap-2"
    >
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
        data-testid={`setting-editor-${setting.key}-input`}
        placeholder={placeholderKey ? t(placeholderKey) : ""}
        className="h-9 text-[13px]"
      />
      <button
        type="submit"
        data-testid={`setting-editor-${setting.key}-commit`}
        aria-label={t("prePublish.settingEditor.confirm")}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        data-testid={`setting-editor-${setting.key}-cancel`}
        aria-label={t("prePublish.settingEditor.cancel")}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted/50"
      >
        <X className="h-4 w-4" />
      </button>
    </form>
  );
};

/** Date : Input type="date" + ✓ */
const DateEditor: React.FC<SettingEditorProps> = ({
  setting,
  onCommit,
  onCancel,
}) => {
  const { t } = useTranslation("polls");
  const initial = setting.value
    ? new Date(setting.value).toISOString().slice(0, 10)
    : "";
  const [value, setValue] = React.useState<string>(initial);
  const [tooEarly, setTooEarly] = React.useState(false);
  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    // Borne basse (expiration : lendemain de la dernière date proposée).
    // Comparaison de chaînes YYYY-MM-DD, sûre pour ce format.
    if (value && setting.min && value < setting.min) {
      setTooEarly(true);
      return;
    }
    // Stocker en ISO complet pour cohérence avec advancedSettings.expiresAt
    onCommit(setting.key, value ? new Date(value).toISOString() : null);
  };
  return (
    <form
      data-testid={`setting-editor-${setting.key}`}
      data-type="date"
      // Message traduit plutôt que la bulle native de `min`.
      noValidate
      onSubmit={handleSubmit}
      className="mt-2 flex flex-wrap items-center gap-2"
    >
      <Input
        type="date"
        autoFocus
        value={value}
        min={setting.min}
        aria-invalid={tooEarly || undefined}
        onChange={(e) => {
          setValue(e.target.value);
          setTooEarly(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
        data-testid={`setting-editor-${setting.key}-input`}
        className="h-9 text-[13px]"
      />
      <button
        type="submit"
        data-testid={`setting-editor-${setting.key}-commit`}
        aria-label={t("prePublish.settingEditor.confirm")}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        data-testid={`setting-editor-${setting.key}-cancel`}
        aria-label={t("prePublish.settingEditor.cancel")}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted/50"
      >
        <X className="h-4 w-4" />
      </button>
      {tooEarly && setting.min && (
        <p
          role="alert"
          data-testid={`setting-editor-${setting.key}-error`}
          className="basis-full text-xs text-destructive"
        >
          {t("prePublish.settingEditor.expires_too_early", {
            date: new Date(`${setting.min}T00:00:00`).toLocaleDateString(),
          })}
        </p>
      )}
    </form>
  );
};

/** Placeholder spécifique selon le setting key */
function settingPlaceholder(key: SettingKey): string {
  switch (key) {
    case "location":
      return "prePublish.settingEditor.placeholder.location";
    case "resultsCopyEmail":
      return "prePublish.settingEditor.placeholder.resultsCopyEmail";
    case "maxResponses":
      return "prePublish.settingEditor.placeholder.maxResponses";
    default:
      return "";
  }
}

export default SettingEditor;
